import { 
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { OrderOptionsValues, HotelDestinationResponse } from "@bandido/shared/interfaces";
import { LoadingButton } from "@mui/lab";

import { useSnackBar } from "../../../common/components/SnackBarContext";
import { useDebouncedState } from "../../../common/hooks/useDebouncedState";
import { useExtractHotels } from "../../mutations";
import { useGetHotelDestinations, useGetHotelSuppliers } from "../../queries";

type VendorHotelsExtractorModalProps = {
  open: boolean;
  onClose: () => void;
};

type DestinationMultiSelectProps = {
  accessCode: string;
  selectedDestinations: HotelDestinationResponse[];
  onDestinationsChange: (destinations: HotelDestinationResponse[]) => void;
  maxSelections?: number;
  disabled?: boolean;
};

const DestinationMultiSelect = ({ 
  accessCode,
  selectedDestinations, 
  onDestinationsChange, 
  maxSelections = 20,
  disabled = false
}: DestinationMultiSelectProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useDebouncedState('', 500);
  const [ destinationsToShow, setDestinationsToShow ] = useState<HotelDestinationResponse[]>([]);

  const { data: destinations, isLoading: isLoadingDestinations } = useGetHotelDestinations({
    searchText: inputValue,
    accessCode: accessCode,
    currentPage: 1,
    pageSize: 50,
    sort: 'texts',
    order: OrderOptionsValues.asc,
  });

  const handleDestinationChange = useCallback((_: any, newValue: HotelDestinationResponse[]) => {
    if (newValue.length <= maxSelections) {
      onDestinationsChange(newValue);
    }
  }, [onDestinationsChange, maxSelections]);

  useEffect(() => {
    setDestinationsToShow(destinations?.items || []);
  }, [destinations]);

  useEffect(() => {
    setInputValue('');
    onDestinationsChange([]);
    setDestinationsToShow([]);
  }, [accessCode]);

  const handleInputChange = useCallback((value: string) => setInputValue(value), [setInputValue]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} key={accessCode}>
      <Autocomplete
        multiple
        id="destinations-multiselect"
        disableCloseOnSelect={true}
        options={destinationsToShow}
        getOptionLabel={(option) => option.hierarchyEnglish || option.hierarchySpanish || option.hierarchyPortuguese}
        value={selectedDestinations}
        onChange={handleDestinationChange}
        onInputChange={(_, newInputValue) => handleInputChange(newInputValue)}
        loading={isLoadingDestinations}
        disabled={disabled}
        filterOptions={(x) => x} // Desactivamos el filtro local ya que filtramos en el backend
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.hierarchyEnglish || option.hierarchySpanish || option.hierarchyPortuguese}
              size="small"
              {...getTagProps({ index })}
              key={option.id}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('hotelContent.extraction.destinations')}
            placeholder={
              selectedDestinations.length === 0 
                ? (t('common.search'))
                : undefined
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingDestinations ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            helperText={`${selectedDestinations.length}/${maxSelections} ${t('common.itemsSelected')}`}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Typography variant="body2">{option.hierarchyEnglish || option.hierarchySpanish || option.hierarchyPortuguese}</Typography>
          </Box>
        )}
        noOptionsText={t('common.noData')}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    </Box>
  );
};

export const VendorHotelsExtractorModal = ({ open, onClose }: VendorHotelsExtractorModalProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { data: suppliers, isLoading: isLoadingSuppliers } = useGetHotelSuppliers({
    currentPage: 1,
    pageSize: 10000,
  });
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedDestinations, setSelectedDestinations] = useState<HotelDestinationResponse[]>([]);
  const { mutate: extractHotels, isLoading: isLoadingExtractHotels } = useExtractHotels();
  
  const handleSupplierChange = (event: SelectChangeEvent) => {
    setSelectedSupplier(event.target.value);
  };
  
  const handleDestinationsChange = (destinations: HotelDestinationResponse[]) => {
    setSelectedDestinations(destinations);
  };

  const handleClose= () => {
    onClose();
    setSelectedSupplier('');
    setSelectedDestinations([]);
  };

  const handleStartExtraction = () => {
    try {
      extractHotels({
        accessCode: selectedSupplier,
        destinationCodes: selectedDestinations.map(dest => dest.code),
      });
      addToast({
        color: 'success',
        message: t('hotelContent.extraction.success'),
      });
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const isLoading = isLoadingSuppliers;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('hotelContent.extraction.newExtraction')}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="supplier-select-label" shrink={true}>
                {t('hotelContent.extraction.supplier')}
              </InputLabel>
              <Select
                labelId="supplier-select-label"
                value={selectedSupplier}
                label={t('hotelContent.extraction.supplier')}
                onChange={handleSupplierChange}
                size="small"
                notched={true}
              >
                {suppliers?.items.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.accessCode}>
                    {supplier.supplierName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DestinationMultiSelect
              accessCode={selectedSupplier}
              selectedDestinations={selectedDestinations}
              onDestinationsChange={handleDestinationsChange}
              maxSelections={20}
              disabled={isLoading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton 
          loading={isLoadingExtractHotels}
          onClick={handleStartExtraction}
          variant="contained"
          disabled={!selectedSupplier || selectedDestinations.length === 0 || isLoading}
        >
          {t('hotelContent.extraction.startExtraction')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
